import _ from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CAPTURE_MODE, ECG_VIEWER_CONFIG_STORE } from '@/shared/ui';
import { KEY_RECOIL, TYPE_BEAT_ENUM, TYPE_RHYTHM_EVENT_ENUM } from '@/shared/config';
import { convertToMomentJs } from '@/shared/lib/aiUtils';
import { useUpdateEffect } from '@/shared/lib/customHooks';
import { toastrError } from '@/shared/lib/toastNotification';
import { checkAndProcessBeatData } from '@/entities/beat';
import { checkAndProcessEcgData, formatDrawDisplay, formatDrawMutation } from '@/entities/ecg';
import { logError } from '@/shared/lib/apiHelpers';
import { isRangesOverlap } from '@/shared/lib/timeRange';
import { TPickDateAction } from '@/shared/model';
import {
  selectAiChannel,
  selectChartHrsData,
  selectEcgObject,
  selectIsActiveTab,
  selectJumpTime,
  selectNextColumnInfo,
  selectPickDateAction,
  selectPickedBookmarkDate,
  selectPickedEndDate,
  selectBeatChanges,
  selectEventOthers,
  selectPickedStartDate,
  selectPrevColumnInfo,
  selectSelectedColumnInfo,
  selectSelectedEventIndex,
  selectSelectedHrTime,
  selectViewHrMode,
  selectBulkBeatsChanges,
  selectResetBulkBeatsChanges,
  useReportStore,
} from '@/entities/report';
import { calculateStartStopStripMomentOfListEvent } from '@/entities/event';
import {
  countBeatChannels,
  generateUpdatedBeatsV2,
  getUpdatedBeatMarkerV2,
  handleAddEventOthersV2,
  handleResetAppliedBuldBeats,
  handleUpdateBeatChanges,
} from '@/widgets/hr-summary/ui/helper';
import type { DailySummaryEcgViewerWrapperProps } from './dailySummaryEcgViewerWrapper';

/**
 * Drives the ECG viewer embedded in the HR summary tab, loading the minute strip behind the selected column and keeping it in sync with beat and event edits. Binds tab-keyed report store state, downloads and trims ECG and beat data for the selected events, rebuilds the viewer object when beats, bookmarks or other-events change, and handles date picking, bookmark and event indicators, jump-to-time and Escape-key cancellation.
 *
 * @param props - The wrapper props with the ECG data map, timezone offset and the parent's arrow-click callback.
 * @returns An object exposing the loading flag, the ECG object, the chart and strip-epoch refs, selection and indicator data, and the capture, date-pick, arrow and beat-update handlers.
 */
const useDailySummaryEcgViewerWrapper = ({
  ecgDataMap,
  handleClickArrowButton,
  timezoneOffset,
}: Pick<
  DailySummaryEcgViewerWrapperProps,
  'ecgDataMap' | 'handleClickArrowButton' | 'timezoneOffset'
>) => {
  const keyRecoil = KEY_RECOIL.HR_SUMMARY;

  const selectedColumnInfo = useReportStore(selectSelectedColumnInfo(keyRecoil));
  const selectedEventIndex = useReportStore(selectSelectedEventIndex(keyRecoil));
  const prevColumnInfo = useReportStore(selectPrevColumnInfo(keyRecoil));
  const nextColumnInfo = useReportStore(selectNextColumnInfo(keyRecoil));
  const setBeatChangesAction = useReportStore((s) => s.actions.setBeatChanges);
  /**
   * Stores the pending beat edits of this tab. Forwards the value to the report store's setBeatChanges action under this tab's key.
   *
   * @param v - The new beat changes, or an updater receiving the current list.
   * @returns Nothing.
   */
  const setBeatChanges = (v: Parameters<typeof setBeatChangesAction>[1]) =>
    setBeatChangesAction(keyRecoil, v);
  /**
   * Reads the pending beat edits recorded for the HR summary tab. Applies the tab-keyed selectBeatChanges selector to the current report store snapshot instead of subscribing to it.
   *
   * @returns The beat changes held for this tab.
   */
  const getBeatChanges = () => selectBeatChanges(keyRecoil)(useReportStore.getState());
  const pickDateAction = useReportStore(selectPickDateAction(keyRecoil));
  const setPickDateActionAction = useReportStore((s) => s.actions.setPickDateAction);
  /**
   * Stores which date pick is armed on the ECG strip, start, end, bookmark or none. Forwards the value to the report store's setPickDateAction action under this tab's key.
   *
   * @param v - The new pick-date action, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setPickDateAction = (v: Parameters<typeof setPickDateActionAction>[1]) =>
    setPickDateActionAction(keyRecoil, v);
  const pickedStartDate = useReportStore(selectPickedStartDate(keyRecoil));
  const setPickedStartDateAction = useReportStore((s) => s.actions.setPickedStartDate);
  /**
   * Stores the start date picked on the ECG strip. Forwards the value to the report store's setPickedStartDate action under this tab's key.
   *
   * @param v - The new start date, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setPickedStartDate = (v: Parameters<typeof setPickedStartDateAction>[1]) =>
    setPickedStartDateAction(keyRecoil, v);
  const pickedEndDate = useReportStore(selectPickedEndDate(keyRecoil));
  const setPickedEndDateAction = useReportStore((s) => s.actions.setPickedEndDate);
  /**
   * Stores the end date picked on the ECG strip. Forwards the value to the report store's setPickedEndDate action under this tab's key.
   *
   * @param v - The new end date, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setPickedEndDate = (v: Parameters<typeof setPickedEndDateAction>[1]) =>
    setPickedEndDateAction(keyRecoil, v);
  const pickedBookmarkDate = useReportStore(selectPickedBookmarkDate(keyRecoil));
  const setPickedBookmarkDateAction = useReportStore((s) => s.actions.setPickedBookmarkDate);
  /**
   * Stores the bookmark date picked on the ECG strip. Forwards the value to the report store's setPickedBookmarkDate action under this tab's key.
   *
   * @param v - The new bookmark date, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setPickedBookmarkDate = (v: Parameters<typeof setPickedBookmarkDateAction>[1]) =>
    setPickedBookmarkDateAction(keyRecoil, v);
  const ecgBookmarks = useReportStore((s) => s.ecgBookmarks);
  const aiChannel = useReportStore(selectAiChannel(keyRecoil));
  const setAiChannelAction = useReportStore((s) => s.actions.setAiChannel);
  /**
   * Stores the dominant beat channel detected for the loaded minute. Forwards the value to the report store's setAiChannel action under this tab's key.
   *
   * @param v - The new AI channel, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setAiChannel = (v: Parameters<typeof setAiChannelAction>[1]) =>
    setAiChannelAction(keyRecoil, v);
  const ecgObject = useReportStore(selectEcgObject(keyRecoil));
  const setEcgObjectAction = useReportStore((s) => s.actions.setEcgObject);
  /**
   * Stores the ECG viewer object rendered for this tab. Forwards the value to the report store's setEcgObject action under this tab's key.
   *
   * @param v - The new ECG viewer object, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setEcgObject = (v: Parameters<typeof setEcgObjectAction>[1]) =>
    setEcgObjectAction(keyRecoil, v);
  const isActiveTab = useReportStore(selectIsActiveTab(keyRecoil));
  /**
   * Reads the per-minute heart-rate series backing the HR chart of this tab. Evaluates the tab-keyed selectChartHrsData selector against the current report store snapshot.
   *
   * @returns The heart-rate chart data points for this tab.
   */
  const getChartHrsData = () => selectChartHrsData(keyRecoil)(useReportStore.getState());
  /**
   * Reads the manually captured other-events stored for the HR summary tab. Evaluates the tab-keyed selectEventOthers selector against the current report store snapshot.
   *
   * @returns The other-events recorded for this tab.
   */
  const getEventOthers = () => selectEventOthers(keyRecoil)(useReportStore.getState());
  const setEventOthersAction = useReportStore((s) => s.actions.setEventOthers);
  /**
   * Stores the captured other-events of this tab. Forwards the value to the report store's setEventOthers action under this tab's key.
   *
   * @param v - The new other-events list, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setEventOthers = (v: Parameters<typeof setEventOthersAction>[1]) =>
    setEventOthersAction(keyRecoil, v);
  /**
   * Reads the minimum heart-rate occurrences recorded for the selected date. Pulls minHrsInDate straight off the current report store snapshot rather than subscribing.
   *
   * @returns The minimum heart-rate entries of the selected date.
   */
  const getMinHrsInDate = () => useReportStore.getState().minHrsInDate;
  /**
   * Reads the maximum heart-rate occurrences recorded for the selected date. Pulls maxHrsInDate straight off the current report store snapshot rather than subscribing.
   *
   * @returns The maximum heart-rate entries of the selected date.
   */
  const getMaxHrsInDate = () => useReportStore.getState().maxHrsInDate;
  /**
   * Reads the timestamp of the heart-rate point the user selected on the HR chart. Evaluates the tab-keyed selectSelectedHrTime selector against the current report store snapshot.
   *
   * @returns The timestamp of the selected heart-rate point.
   */
  const getSelectedHrTime = () => selectSelectedHrTime(keyRecoil)(useReportStore.getState());
  /**
   * Reads whether the HR summary is currently showing maximum or minimum heart rate. Evaluates the tab-keyed selectViewHrMode selector against the current report store snapshot.
   *
   * @returns The active heart-rate view mode.
   */
  const getViewHrMode = () => selectViewHrMode(keyRecoil)(useReportStore.getState());
  const controlModalHrSummary = useReportStore((s) => s.controlHrSummaryModal);
  const setControlModalHrSummary = useReportStore((s) => s.actions.setControlHrSummaryModal);
  const jumpTime = useReportStore(selectJumpTime(keyRecoil));
  const setJumpTimeAction = useReportStore((s) => s.actions.setJumpTime);
  /**
   * Stores the epoch the ECG viewer should scroll to. Forwards the value to the report store's setJumpTime action under this tab's key.
   *
   * @param v - The new jump epoch, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setJumpTime = (v: Parameters<typeof setJumpTimeAction>[1]) =>
    setJumpTimeAction(keyRecoil, v);
  const selectedEvent = useMemo(
    () => (_.isEmpty(selectedColumnInfo) ? null : selectedColumnInfo[selectedEventIndex]),
    [selectedColumnInfo, selectedEventIndex],
  );
  const setEventOptionsAction = useReportStore((s) => s.actions.setEventOptions);
  /**
   * Stores the event options snapshot the undo and redo stack replays. Forwards the value to the report store's setEventOptions action under this tab's key.
   *
   * @param v - The new event options, or an updater receiving the current ones.
   * @returns Nothing.
   */
  const setEventOptions = (v: Parameters<typeof setEventOptionsAction>[1]) =>
    setEventOptionsAction(keyRecoil, v);
  const beatChanges = useReportStore(selectBeatChanges(keyRecoil));
  const otherEvents = useReportStore(selectEventOthers(keyRecoil));
  /**
   * Reads the captured other-events used to draw overlays on the ECG strip. Evaluates the tab-keyed selectEventOthers selector against the current report store snapshot.
   *
   * @returns The other-events recorded for this tab.
   */
  const getOtherEvents = () => selectEventOthers(keyRecoil)(useReportStore.getState());
  const isRenderViewerEcg = useReportStore((s) => s.isRenderViewerEcg);
  /**
   * Reads the bulk beat edits queued for the HR summary tab. Applies the tab-keyed selectBulkBeatsChanges selector to the current report store snapshot instead of subscribing to it.
   *
   * @returns The bulk beat changes recorded for this tab.
   */
  const getBulkBeatsChanges = () => selectBulkBeatsChanges(keyRecoil)(useReportStore.getState());
  /**
   * Reads the record of bulk beat changes that have been reverted for this tab. Evaluates the tab-keyed selectResetBulkBeatsChanges selector against the current report store snapshot.
   *
   * @returns The reset bulk beat changes tracked for this tab.
   */
  const getResetBulkBeatsChanges = () =>
    selectResetBulkBeatsChanges(keyRecoil)(useReportStore.getState());
  const setBeatOptionsAction = useReportStore((s) => s.actions.setBeatOptions);
  /**
   * Stores the beat options snapshot the undo and redo stack replays. Forwards the value to the report store's setBeatOptions action under this tab's key.
   *
   * @param v - The new beat options, or an updater receiving the current ones.
   * @returns Nothing.
   */
  const setBeatOptions = (v: Parameters<typeof setBeatOptionsAction>[1]) =>
    setBeatOptionsAction(keyRecoil, v);
  const setResetBulkBeatsChangesAction = useReportStore((s) => s.actions.setResetBulkBeatsChanges);
  /**
   * Stores the record of bulk beat changes that have been reverted for this tab. Forwards the value to the report store's setResetBulkBeatsChanges action under this tab's key.
   *
   * @param v - The new reset bulk beat changes, or an updater receiving the current ones.
   * @returns Nothing.
   */
  const setResetBulkBeatsChanges = (v: Parameters<typeof setResetBulkBeatsChangesAction>[1]) =>
    setResetBulkBeatsChangesAction(keyRecoil, v);

  const [isLoading, setIsLoading] = useState(true);
  const [isDisablePrevBtn, setIsDisablePrevBtn] = useState(false);
  const [isDisableNextBtn, setIsDisableNextBtn] = useState(false);

  const ecgChartRef = useRef<any>();
  const startStripEpoch = useRef(null);
  const stopStripEpoch = useRef(null);
  const isJumpToEnd = useRef(false);
  const isJumpToStart = useRef(false);
  const originalBeatMarkers = useRef<any[]>([]);
  const originalHesBeatStatus = useRef<any[]>([]);
  const originalBeatHeatMapIds = useRef<any[]>([]);
  const beatMarkersInEcgViewer = useRef<any[]>([]);
  const hesBeatStatusInEcgViewer = useRef<any[]>([]);
  const ecgObjectRef = useRef<any>({});
  const currentBeatsChangeRef = useRef<any>();

  let isActiveQuery: boolean;

  const isPickingDate =
    pickDateAction === TPickDateAction.START ||
    pickDateAction === TPickDateAction.END ||
    pickDateAction === TPickDateAction.BOOKMARK;

  //* Format event to be drawed in ecgviewer
  /**
   * Converts an ECG event or minute record into the sample-offset marker the ECG viewer draws. Colours artifacts gray, AFib orange and everything else red, clamps the event start and stop epochs to the loaded strip window before scaling them by the sampling frequency, and leaves both positions at zero for minute records that carry no start and stop.
   *
   * @param event - The event or minute record to place, read for its type, start, stop and id.
   * @param isSelected - Whether this entry is the currently selected event, passed straight through to the viewer.
   * @returns An object carrying startPos, stopPos, color, id, isSelected and the jump portion for the viewer.
   */
  const getEventInfoInEcgViewer = ({ event, isSelected = false }: any) => {
    const { samplingFrequency } = ecgDataMap;
    const isEvent = event.start && event.stop; // not minute info (minute info dont have start and stop)
    let startPos = 0;
    let stopPos = 0;
    let color;
    let isHr;
    if (isEvent) {
      const isArtifactEvent = event.type === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT;
      const isAfibEvent = event.type === TYPE_RHYTHM_EVENT_ENUM.AFIB;
      isHr = event.type === TYPE_RHYTHM_EVENT_ENUM.MAX_HR || TYPE_RHYTHM_EVENT_ENUM.MIN_HR;
      color = isArtifactEvent ? 'gray' : isAfibEvent ? 'orange' : 'red';
      const startEpoch = new Date(event.start).getTime();
      const stopEpoch = new Date(event.stop).getTime();
      if (startStripEpoch.current <= startEpoch) {
        const eventDiffSeconds = Math.abs(startEpoch - startStripEpoch.current) / 1000;
        startPos = Math.round(eventDiffSeconds * samplingFrequency);
      }
      if (stopEpoch >= stopStripEpoch.current) {
        stopPos = (stopStripEpoch.current - startStripEpoch.current) / 1000;
      } else {
        stopPos = (stopEpoch - startStripEpoch.current) / 1000;
      }
      stopPos = Math.round(stopPos * samplingFrequency);
    }
    return {
      startPos,
      stopPos,
      color,
      id: event.id,
      isSelected,
      portion: isHr ? 0 : 10, // Determine start window when jump to
    };
  };

  const createObjectEcg = ({
    ecg,
    beats,
    hesBeatStatus,
    events,
    eventIndex,
    beatHeatMapIds,
    exportEcgData,
  }: any) => {
    const { channels, gain, samplingFrequency } = ecgDataMap;
    // *: Restore data from previous editing session
    const { updatedBeats, updatedHesBeatStatus } = generateUpdatedBeatsV2({
      beats,
      hesBeatStatus,
      beatChanges: getBeatChanges(),
      startEpoch: startStripEpoch.current,
      stopEpoch: stopStripEpoch.current,
      beatHeatMapIds,
      applyBulkBeatsChanges: getBulkBeatsChanges(),
      resetAppliedBulkBeatsChanges: getResetBulkBeatsChanges(),
    });
    beatMarkersInEcgViewer.current = updatedBeats;
    hesBeatStatusInEcgViewer.current = updatedHesBeatStatus;
    const eventsInfo = _.map(events, (event: any, index: any) =>
      getEventInfoInEcgViewer({ event, isSelected: index === eventIndex }),
    );
    const endStripPos =
      ((stopStripEpoch.current - startStripEpoch.current) / 1000) * samplingFrequency;
    const otherEvents = getOtherEvents();
    /**
     * Collects the strip drawings attached to the captured other-events so they can be rendered over the ECG waveform. Flattens each event's draw array and discards falsy entries, defaulting to an empty array.
     *
     * @returns The flattened list of drawing payloads taken from the other-events.
     */
    const drawList =
      _.filter(_.flatten(_.map(otherEvents, (event: any) => event.draw)), (x: any) => !!x) || [];
    const { draw } = formatDrawDisplay(
      drawList,
      startStripEpoch.current,
      samplingFrequency,
      stopStripEpoch.current,
    );
    const viewerObject = {
      options: {},
      widgets: draw,
    };
    const newEcgObject = {
      dataSamples: ecg,
      adcGain: gain,
      adcZero: 0,
      numChannels: channels.length,
      diagnosticLead: 2,
      samplingRate: samplingFrequency,
      events: eventsInfo,
      aiPredict: [
        {
          beats: updatedBeats,
          hesBeatStatus: updatedHesBeatStatus,
          channel: 1,
          hesEventStatus: _.times(updatedHesBeatStatus.length, () => 1),
        },
      ],
      isAppliedAIByDefault: true,
      isShowVES: true,
      isShowSVES: true,
      isShowBeatMarkers: true,
      jumpOffset: isJumpToStart.current ? 1 : isJumpToEnd.current ? endStripPos : 0,
      hiddenChannels: ECG_VIEWER_CONFIG_STORE.visibleChannels,
      hpFilters: ECG_VIEWER_CONFIG_STORE.hiPValue,
      notchFilters: ECG_VIEWER_CONFIG_STORE.loPValue,
      speed: ECG_VIEWER_CONFIG_STORE.spdValue,
      amp: ECG_VIEWER_CONFIG_STORE.ampValue,
      wndDuration: ECG_VIEWER_CONFIG_STORE.wndDuration,
      viewerObject,
      exportEcgData,
    };
    setEcgObject(newEcgObject);
    ecgObjectRef.current = newEcgObject;
  };

  /**
   * Loads the ECG samples and beats for the minute behind the selected column and builds the viewer object from them. Appends the day's min or max heart-rate entries overlapping that minute, re-resolves the selected index against the chosen HR time, fetches ECG and beat data in parallel, trims both to the computed strip window, merges overlapping events from the neighbouring columns, sets the AI channel, and enables or disables the previous and next buttons from the surrounding minutes.
   *
   * @param events - The selected column's events, whose last entry is the minute record being loaded.
   * @param eventIndex - Index of the event to select once loaded, re-resolved for min and max HR views.
   * @returns A promise resolving once the strip has been loaded and the viewer object built.
   */
  const downloadDataEcgV2 = async (events: any, eventIndex: any = 0) => {
    if (_.isEmpty(events)) {
      return;
    }
    setIsLoading(true);
    try {
      const maxHrsInDate = getMaxHrsInDate();
      const minHrsInDate = getMinHrsInDate();
      const minuteInfo = events[events.length - 1];
      const startMinute = minuteInfo.id;
      const stopMinute = startMinute + 60000;
      const totalEvents = [...events];
      const viewHrMode = getViewHrMode();
      //* push min max hr to events array to show in ecg viewer
      if (viewHrMode === TYPE_RHYTHM_EVENT_ENUM.MAX_HR) {
        _.forEach(maxHrsInDate, (x: any) => {
          const startHrEpoch = moment(x.start).valueOf();
          const stopHrEpoch = moment(x.stop).valueOf();
          if (isRangesOverlap(startMinute, stopMinute, startHrEpoch, stopHrEpoch)) {
            totalEvents.push({
              ...minuteInfo,
              id: startHrEpoch,
              start: x.start,
              stop: x.stop,
              type: TYPE_RHYTHM_EVENT_ENUM.MAX_HR,
            });
          }
        });
      }
      if (viewHrMode === TYPE_RHYTHM_EVENT_ENUM.MIN_HR) {
        _.forEach(minHrsInDate, (x: any) => {
          const startHrEpoch = moment(x.start).valueOf();
          const stopHrEpoch = moment(x.stop).valueOf();
          if (isRangesOverlap(startMinute, stopMinute, startHrEpoch, stopHrEpoch)) {
            totalEvents.push({
              ...minuteInfo,
              id: startHrEpoch,
              start: x.start,
              stop: x.stop,
              type: TYPE_RHYTHM_EVENT_ENUM.MIN_HR,
            });
          }
        });
      }
      //* Re calculate index and set jump time of min max hr event
      let newEventIndex = eventIndex;
      if (
        viewHrMode === TYPE_RHYTHM_EVENT_ENUM.MAX_HR ||
        viewHrMode === TYPE_RHYTHM_EVENT_ENUM.MIN_HR
      ) {
        const selectedHrTime = getSelectedHrTime();
        const foundIndex = _.findIndex(totalEvents, (x: any) => {
          if (
            x.type === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT &&
            totalEvents[eventIndex]?.type === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT
          ) {
            return x.startTimeEpoch === moment(selectedHrTime).startOf('minute').valueOf();
          }
          if (
            x.type === TYPE_RHYTHM_EVENT_ENUM.MAX_HR ||
            x.type === TYPE_RHYTHM_EVENT_ENUM.MIN_HR
          ) {
            return x.start === selectedHrTime;
          }
          return false;
        });
        if (foundIndex > -1) {
          newEventIndex = foundIndex;
        }
      }
      // console.log('[dailySummaryEcgViewerWrapper]-downloadData', totalEvents);

      const { startStrip, stopStrip } = calculateStartStopStripMomentOfListEvent({
        events: totalEvents,
      });

      // const { defaultStartStrip, defaultStopStrip } = calculateStartStopStripMomentOfListEventV2({
      //   startTimeEpoch: minuteInfo.startTimeEpoch,
      // });

      const defaultStartStrip = startStrip;
      const defaultStopStrip = stopStrip;

      const promise = [
        checkAndProcessEcgData({
          ecgDataMap,
          dataLength:
            ((defaultStopStrip.valueOf() - defaultStartStrip.valueOf()) / 1000) *
            ecgDataMap.samplingFrequency,
          momentObject: {
            startMoment: defaultStartStrip,
            stopMoment: defaultStopStrip,
          } as any,
        }),
        checkAndProcessBeatData({
          ecgDataMap,
          momentObject: {
            startMoment: defaultStartStrip,
            stopMoment: defaultStopStrip,
          } as any,
        }),
      ];

      const [ecgData, holterBeats]: [any, any] = (await Promise.all(promise)) as any;
      if (isActiveQuery) {
        // const { startStrip, stopStrip } = calculateStartStopStripMomentOfEvent(minuteInfo);
        const { startStrip, stopStrip } = calculateStartStopStripMomentOfListEvent({
          events: totalEvents,
        });
        const startTimeString = startStrip.toISOString();
        const stopTimeString = stopStrip.toISOString();
        //* push event in next/previous column if overlap current minute time range
        _.forEach(nextColumnInfo, (event: any) => {
          if (
            !_.find(totalEvents, (x: any) => x.idEvent === event.idEvent) &&
            (isRangesOverlap as any)(event.start, event.stop, startTimeString, stopTimeString)
          ) {
            totalEvents.push(event);
          }
        });
        _.forEach(prevColumnInfo, (event: any) => {
          if (
            !_.find(totalEvents, (x: any) => x.idEvent === event.idEvent) &&
            (isRangesOverlap as any)(event.start, event.stop, startTimeString, stopTimeString)
          ) {
            totalEvents.push(event);
          }
        });
        startStripEpoch.current = startStrip.valueOf();
        stopStripEpoch.current = stopStrip.valueOf();
        // * Cut ecgdata and beat in range
        const cuttedMsStart = startStripEpoch.current - defaultStartStrip.valueOf();
        const cuttedMsStop = defaultStopStrip.valueOf() - stopStripEpoch.current;
        const cuttedSampleStart = (cuttedMsStart / 1000) * ecgDataMap.samplingFrequency;
        const cuttedSampleStop = (cuttedMsStop / 1000) * ecgDataMap.samplingFrequency;
        const cuttedEcgData = _.map(ecgData, (subArr: any) =>
          subArr.slice(cuttedSampleStart, subArr.length - cuttedSampleStop),
        );
        const cuttedBeatSample: any[] = [];
        const cuttedHesBeatStatus: any[] = [];
        const cuttedBeatChannel: any[] = [];
        const cuttedBeatHeatMapIds: any[] = [];
        if (!_.isEmpty(holterBeats?.beats)) {
          _.forEach(holterBeats.beats, (sample: any, index: any) => {
            const epochBeat =
              defaultStartStrip.valueOf() + (sample * 1000) / ecgDataMap.samplingFrequency;
            if (epochBeat >= startStripEpoch.current && epochBeat <= stopStripEpoch.current) {
              cuttedHesBeatStatus.push(holterBeats?.hesBeatStatus[index]);
              cuttedBeatChannel.push(holterBeats?.beatChannels[index]);
              cuttedBeatSample.push(
                Math.round(
                  (((epochBeat as any) - startStripEpoch.current) / 1000) *
                    ecgDataMap.samplingFrequency,
                ),
              );
              cuttedBeatHeatMapIds.push(holterBeats?.beatHeatMapIds[index]);
            }
          });
        }
        const cuttedHolterBeats = {
          beats: cuttedBeatSample,
          hesBeatStatus: cuttedHesBeatStatus,
          beatChannels: cuttedBeatChannel,
          beatHeatMapIds: cuttedBeatHeatMapIds,
        };
        //* Main channel of minute
        setAiChannel(countBeatChannels(cuttedHolterBeats?.beatChannels) as any);
        const exportStart = startStripEpoch.current - 10000;
        const exportStop = stopStripEpoch.current + 10000;
        const ecgExport = await checkAndProcessEcgData({
          ecgDataMap,
          dataLength: ((exportStart - exportStop) / 1000) * ecgDataMap.samplingFrequency,
          momentObject: {
            startMoment: moment(exportStart),
            stopMoment: moment(exportStop),
          } as any,
        });
        createObjectEcg({
          ecg: cuttedEcgData,
          beats: cuttedHolterBeats?.beats,
          hesBeatStatus: cuttedHolterBeats?.hesBeatStatus,
          events: _.uniqWith(totalEvents, (a: any, b: any) => {
            const aCopy = { id: a?.id, type: a?.type, idEvent: a?.idEvent };
            const bCopy = { id: b?.id, type: b?.type, idEvent: b?.idEvent };
            return _.isEqual(aCopy, bCopy);
          }),
          eventIndex: newEventIndex,
          beatHeatMapIds: cuttedHolterBeats?.beatHeatMapIds,
          exportEcgData: {
            ecgData: ecgExport,
          },
        });
        originalBeatMarkers.current = cuttedHolterBeats?.beats;
        originalHesBeatStatus.current = cuttedHolterBeats?.hesBeatStatus;
        originalBeatHeatMapIds.current = cuttedHolterBeats?.beatHeatMapIds;
        //* Check can click next or previous minute
        const hrsData = getChartHrsData();
        const prevMinute = minuteInfo.startTimeEpoch - 60000;
        const nextMinute = minuteInfo.startTimeEpoch + 60000;
        const prevMinuteHrData = _.find(hrsData, (x: any) => x.id === prevMinute);
        const nextMinuteHrData = _.find(hrsData, (x: any) => x.id === nextMinute);
        setIsDisablePrevBtn(!prevMinuteHrData?.avgHr);
        setIsDisableNextBtn(!nextMinuteHrData?.avgHr);
        setIsLoading(false);
      }
      isJumpToEnd.current = false;
      isJumpToStart.current = false;
    } catch (error) {
      if (!_.includes(error.message, 'canceled')) {
        logError('Failed to download ECG data: ', error);
      }
      setIsLoading(false);
    }
  };
  /**
   * Rebuilds the ECG viewer object from the original beats so the strip reflects the current edits and viewer settings when the tab is left. Regenerates beats and HES statuses from the stored beat, bulk and reset changes, then clones the existing ECG object with the current channel, filter, speed, amplitude and window settings before storing it.
   *
   * @returns Nothing.
   */
  const reCreateObjectEcg = () => {
    if (!_.isEmpty(ecgObject)) {
      const { updatedBeats, updatedHesBeatStatus } = generateUpdatedBeatsV2({
        beats: originalBeatMarkers.current,
        beatHeatMapIds: originalBeatHeatMapIds.current,
        hesBeatStatus: originalHesBeatStatus.current,
        beatChanges: getBeatChanges(),
        startEpoch: startStripEpoch.current,
        stopEpoch: stopStripEpoch.current,
        applyBulkBeatsChanges: getBulkBeatsChanges(),
        resetAppliedBulkBeatsChanges: getResetBulkBeatsChanges(),
      });
      beatMarkersInEcgViewer.current = updatedBeats;
      hesBeatStatusInEcgViewer.current = updatedHesBeatStatus;
      const clonedEcgObject = _.cloneDeep(ecgObject);
      _.assign(clonedEcgObject, {
        hiddenChannels: ECG_VIEWER_CONFIG_STORE.visibleChannels,
        hpFilters: ECG_VIEWER_CONFIG_STORE.hiPValue,
        notchFilters: ECG_VIEWER_CONFIG_STORE.loPValue,
        speed: ECG_VIEWER_CONFIG_STORE.spdValue,
        amp: ECG_VIEWER_CONFIG_STORE.ampValue,
        wndDuration: ECG_VIEWER_CONFIG_STORE.wndDuration,
        aiPredict: [
          {
            beats: updatedBeats,
            hesBeatStatus: updatedHesBeatStatus,
            channel: 1,
            hesEventStatus: _.times(updatedHesBeatStatus.length, () => 1),
          },
        ],
      });
      setEcgObject(clonedEcgObject);
      ecgObjectRef.current = clonedEcgObject;
    }
  };

  /**
   * Records the date the user clicked inside the ECG strip while a start, end or bookmark pick is armed. Offsets the strip start epoch by the clicked second offset in the study timezone and stores the resulting ISO string into the picked start, end or bookmark date according to the active pick action.
   *
   * @param diffSeconds - Offset in seconds from the start of the loaded strip to the clicked position.
   * @returns Nothing.
   */
  const handlePickDate = (diffSeconds: any) => {
    if (isPickingDate) {
      const date = convertToMomentJs(startStripEpoch.current, timezoneOffset)
        .add(diffSeconds, 'seconds')
        .toISOString();
      switch (pickDateAction) {
        case TPickDateAction.START: {
          setPickedStartDate(date);
          break;
        }
        case TPickDateAction.END: {
          setPickedEndDate(date);
          break;
        }
        case TPickDateAction.BOOKMARK: {
          setPickedBookmarkDate(date);
          break;
        }
        default: {
          break;
        }
      }
    }
  };

  /**
   * Applies the beat markers the ECG viewer reports after an edit into the tab's pending beat changes. Diffs the incoming markers and HES statuses against the ones currently drawn, falling back to the originals, refreshes the viewer refs, recomputes which applied bulk beat changes are now reverted, and writes both the merged beat changes and the reset record into the store and the beat options snapshot.
   *
   * @param updatedBeatObject - The viewer payload carrying currentBeatMarkers and currentHesBeatStatus after the edit.
   * @returns Nothing.
   */
  const handleUpdateBeat = (updatedBeatObject: any) => {
    const { currentBeatMarkers, currentHesBeatStatus } = updatedBeatObject;

    const updatedBeatMarkers = getUpdatedBeatMarkerV2(
      currentBeatMarkers,
      _.isEmpty(beatMarkersInEcgViewer.current)
        ? originalBeatMarkers.current
        : beatMarkersInEcgViewer.current,
      currentHesBeatStatus,
      _.isEmpty(hesBeatStatusInEcgViewer.current)
        ? originalHesBeatStatus.current
        : hesBeatStatusInEcgViewer.current,
      originalHesBeatStatus.current,
      startStripEpoch.current,
      originalBeatHeatMapIds.current,
      originalBeatMarkers.current,
    );

    beatMarkersInEcgViewer.current = currentBeatMarkers;
    hesBeatStatusInEcgViewer.current = currentHesBeatStatus;
    const resetBulkChanges = handleResetAppliedBuldBeats({
      appliedBuldBeats: getResetBulkBeatsChanges(),
      strips: updatedBeatMarkers,
      type: updatedBeatMarkers?.[0]?.type,
      bulkBeatsChanges: getBulkBeatsChanges(),
    });
    let beatsChanges: any[] = [];
    setBeatChanges((currBeatChange: any) => {
      const beats = handleUpdateBeatChanges(updatedBeatMarkers, currBeatChange);
      beatsChanges = beats;
      currentBeatsChangeRef.current = beats;
      return beats;
    });

    setResetBulkBeatsChanges(resetBulkChanges);
    setBeatOptions((prev: any) => ({
      ...prev,
      beatChangesState: beatsChanges,
      resetBulkBeatsChangesState: resetBulkChanges,
    }));
  };

  /**
   * Steps the ECG viewer to the neighbouring minute strip when the user clicks a navigation arrow. Raises the jump-to-start ref for a forward move or the jump-to-end ref for a backward one, then delegates to the parent's handleClickArrowButton callback.
   *
   * @param isNext - Whether the user asked for the next minute rather than the previous one.
   * @returns Nothing.
   */
  const clickArrowButtonHandle = useCallback((isNext: any) => {
    if (isNext) {
      isJumpToStart.current = true;
    } else {
      isJumpToEnd.current = true;
    }
    handleClickArrowButton(isNext);
  }, []);

  /**
   * Decides which capture mode the strip capture dialog opens in for the currently selected event. Yields the artifact mode for artifact or deleted-beat types, the event mode for any other typed event, and the other mode when the selection carries no type at all.
   *
   * @returns The capture mode constant matching the selected event.
   */
  const getCaptureMode = () => {
    // selectedColumnInfoState is typed (IEvent | IPoint)[] but IPoint has no `type` — pre-existing gap.
    if ((selectedEvent as any)?.type) {
      if (
        (selectedEvent as any).type === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT ||
        (selectedEvent as any).type === TYPE_BEAT_ENUM.DELETED
      ) {
        return CAPTURE_MODE.ARTIFACT;
      }
      return CAPTURE_MODE.EVENT;
    }
    return CAPTURE_MODE.OTHER;
  };

  const handleCaptureImages = ({
    draw,
    allIndexStripSvg,
    technicianComments,
    captureMode,
    captureTitle,
    visibleChannels,
  }: any) => {
    try {
      if (allIndexStripSvg.length > 0 && captureMode === CAPTURE_MODE.OTHER) {
        const { samplingFrequency } = ecgDataMap;
        const { formatDraw, stripStart, stripStop } = formatDrawMutation(
          draw,
          samplingFrequency,
          startStripEpoch.current,
          timezoneOffset,
          visibleChannels,
        );
        const startTime = stripStart;
        const endTime = stripStop;
        const data = handleAddEventOthersV2({
          events: getEventOthers(),
          draw: formatDraw,
          allIndexStripSvg,
          technicianComments,
          captureTitle,
          startTime,
          endTime,
        } as any);
        setEventOptions((prevOptions: any) => ({
          ...prevOptions,
          eventOthersState: data,
        }));
        setEventOthers(data);
      }
    } catch (error) {
      logError('Failed to capture images: ', error);
      toastrError(error.message, 'Error');
    }
  };

  useEffect(() => {
    isActiveQuery = true;
    if (isRenderViewerEcg) {
      downloadDataEcgV2(selectedColumnInfo, selectedEventIndex);
    }
    return () => {
      isActiveQuery = false;
    };
  }, [selectedColumnInfo, selectedEventIndex, isRenderViewerEcg]);

  /**
   * Redraws the ECG viewer after beats, bookmarks or other-events change, keeping the current scroll offset. Regenerates beats and HES statuses from the stored beat, bulk and reset changes, rebuilds the overlay widgets from the other-events draw list, clones the cached ECG object with the current viewer settings, and resets the chart's interaction mode when the beats themselves changed.
   *
   * @param isChangeBeats - Whether the redraw follows a beat edit, which also resets the chart's interaction mode.
   * @returns Nothing.
   */
  const updateBeats = (isChangeBeats?: any) => {
    if (!_.isEmpty(ecgObjectRef.current)) {
      const { updatedBeats, updatedHesBeatStatus } = generateUpdatedBeatsV2({
        beats: originalBeatMarkers.current,
        hesBeatStatus: originalHesBeatStatus.current,
        beatHeatMapIds: originalBeatHeatMapIds.current,
        beatChanges: getBeatChanges(),
        startEpoch: startStripEpoch.current,
        stopEpoch: stopStripEpoch.current,
        applyBulkBeatsChanges: getBulkBeatsChanges(),
        resetAppliedBulkBeatsChanges: getResetBulkBeatsChanges(),
      });
      beatMarkersInEcgViewer.current = updatedBeats;
      hesBeatStatusInEcgViewer.current = updatedHesBeatStatus;
      const { samplingFrequency } = ecgDataMap;
      /**
       * Collects the strip drawings of the captured other-events while the ECG object is rebuilt after a beat edit. Flattens each event's draw array and discards falsy entries, defaulting to an empty array.
       *
       * @returns The flattened list of drawing payloads taken from the other-events.
       */
      const drawList =
        _.filter(_.flatten(_.map(otherEvents, (event: any) => event.draw)), (x: any) => !!x) || [];
      const { draw } = formatDrawDisplay(
        drawList,
        startStripEpoch.current,
        samplingFrequency,
        stopStripEpoch.current,
      );
      const viewerObject = {
        options: {},
        widgets: draw,
      };
      const clonedEcgObject = _.cloneDeep(ecgObjectRef.current);
      _.assign(clonedEcgObject, {
        jumpOffset: ecgChartRef.current?.getOffsetX() || 1,
        aiPredict: [
          {
            beats: updatedBeats,
            hesBeatStatus: updatedHesBeatStatus,
            channel: 1,
            hesEventStatus: _.times(updatedHesBeatStatus.length, () => 1),
          },
        ],
        hiddenChannels: ECG_VIEWER_CONFIG_STORE.visibleChannels,
        hpFilters: ECG_VIEWER_CONFIG_STORE.hiPValue,
        notchFilters: ECG_VIEWER_CONFIG_STORE.loPValue,
        speed: ECG_VIEWER_CONFIG_STORE.spdValue,
        amp: ECG_VIEWER_CONFIG_STORE.ampValue,
        wndDuration: ECG_VIEWER_CONFIG_STORE.wndDuration,

        viewerObject,
      });
      ecgObjectRef.current = clonedEcgObject;
      if (isChangeBeats) {
        if (ecgChartRef.current) {
          (ecgChartRef.current as any).resetInMode(true);
        }
      }
      setEcgObject(clonedEcgObject);
    }
  };

  // Re-create ECG object when beat changes
  useUpdateEffect(() => {
    if (isRenderViewerEcg) {
      updateBeats();
    }
  }, [ecgBookmarks, otherEvents, isRenderViewerEcg]);

  useUpdateEffect(() => {
    if (isRenderViewerEcg) {
      if (!_.isEqual(beatChanges, currentBeatsChangeRef.current)) {
        currentBeatsChangeRef.current = beatChanges;
        updateBeats(true);
      }
    }
  }, [beatChanges]);

  useUpdateEffect(() => {
    if (!isActiveTab) {
      reCreateObjectEcg();
    } else if (ecgChartRef.current) {
      (ecgChartRef.current as any).resetInMode();
    }
  }, [isActiveTab]);

  useUpdateEffect(() => {
    /**
     * Scrolls the ECG chart to the requested jump time once the strip covering it has finished loading. Converts the jump epoch into a sample offset with the sampling frequency, clears the stored jump time afterwards, and retries every 10 milliseconds while the chart has not loaded.
     *
     * @returns Nothing.
     */
    const handleJumpToEcgViewer = () => {
      if (jumpTime <= stopStripEpoch.current && jumpTime >= startStripEpoch.current) {
        if ((ecgChartRef.current as any)?.hasLoadChart()) {
          const jumpPos = Math.round(
            (((jumpTime as any) - startStripEpoch.current) / 1000) * ecgDataMap.samplingFrequency,
          );
          (ecgChartRef.current as any).handleJumpTo(jumpPos, 0);
          setJumpTime(undefined);
        } else {
          setTimeout(() => {
            handleJumpToEcgViewer();
          }, 10);
        }
      }
    };
    if (
      jumpTime &&
      !_.isEmpty(ecgChartRef.current) &&
      startStripEpoch.current &&
      stopStripEpoch.current &&
      !isLoading
    ) {
      handleJumpToEcgViewer();
    }
  }, [
    jumpTime,
    isLoading,
    ecgObject, // trigger useEffect when data has cached
  ]);

  const eventIndicator = useMemo(() => {
    const { samplingFrequency } = ecgDataMap;
    let startPos = null;
    let endPos = null;
    if (pickedStartDate) {
      const pickedStartEpoch = new Date(pickedStartDate).getTime();
      startPos = Math.round(
        ((pickedStartEpoch - startStripEpoch.current) / 1000) * samplingFrequency,
      );
    }
    if (pickedEndDate) {
      const pickedEndEpoch = new Date(pickedEndDate).getTime();
      endPos = Math.round(((pickedEndEpoch - startStripEpoch.current) / 1000) * samplingFrequency);
    }
    return {
      startPos,
      endPos,
    };
  }, [pickedStartDate, pickedEndDate, ecgObject]);

  const bookmarksInRange = useMemo(() => {
    const result: any[] = [];
    _.forEach(ecgBookmarks, (bookmark: any) => {
      if (bookmark.center >= startStripEpoch.current && bookmark.center <= stopStripEpoch.current) {
        result.push(bookmark);
      }
    });
    return result;
  }, [ecgBookmarks, ecgObject]);

  const bookmarkIndicators = useMemo(() => {
    const { samplingFrequency } = ecgDataMap;
    const listBookmarks = [];
    if (pickedBookmarkDate) {
      const pickedEpoch = new Date(pickedBookmarkDate).getTime();
      listBookmarks.push({
        sample: Math.round(((pickedEpoch - startStripEpoch.current) / 1000) * samplingFrequency),
        id: 'marker',
      });
    }
    _.forEach(bookmarksInRange, (bookmark: any) => {
      listBookmarks.push({
        sample: Math.round(
          ((bookmark.center - startStripEpoch.current) / 1000) * samplingFrequency,
        ),
        id: bookmark.center,
      });
    });
    return listBookmarks;
  }, [pickedBookmarkDate, bookmarksInRange, ecgObject]);

  useEffect(() => {
    /**
     * Cancels an armed date pick when the user presses Escape over the ECG strip. Ignores auto-repeat key events and clears the pick action only while one is active.
     *
     * @param event - The keyup event carrying the pressed key and its repeat flag.
     * @returns Nothing.
     */
    const handleKeyup = (event: any) => {
      if (event?.repeat) {
        return;
      }
      const { key } = event;
      if (key === 'Escape' && pickDateAction) {
        setPickDateAction(TPickDateAction.NONE);
      }
    };
    if (isPickingDate) {
      window.addEventListener('keyup', handleKeyup);
    }
    return () => {
      window.removeEventListener('keyup', handleKeyup);
    };
  }, [pickDateAction, isPickingDate]);

  return {
    isLoading,
    ecgObject,
    startStripEpoch,
    controlModalHrSummary,
    setControlModalHrSummary,
    selectedColumnInfo,
    ecgChartRef,
    pickDateAction,
    aiChannel,
    isDisableNextBtn,
    isDisablePrevBtn,
    eventIndicator,
    bookmarkIndicators,
    getCaptureMode,
    handleCaptureImages,
    handlePickDate,
    clickArrowButtonHandle,
    handleUpdateBeat,
  };
};

export default useDailySummaryEcgViewerWrapper;
